import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ArticleCard from "@/components/ArticleCard";
import Seo from "@/components/Seo";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async (term) => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        api.get("/products", { params: { q: term, page_size: 60 } }),
        api.get("/articles", { params: { q: term } }),
      ]);
      setProducts(p.data.items);
      setArticles(a.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialQ) run(initialQ);
  }, [initialQ]);

  const submit = (e) => {
    e.preventDefault();
    setParams({ q });
    run(q);
  };

  return (
    <div data-testid="search-page">
      <Seo
        title={initialQ ? `Search: ${initialQ}` : "Search"}
        description="Search across all costumes, articles, and guides on PartyCostumeShop."
        path={initialQ ? `/search?q=${encodeURIComponent(initialQ)}` : "/search"}
        noindex={!initialQ}
      />
      <section className="border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="font-mono uppercase text-[11px] tracking-widest pcs-text-blue mb-3">
            Search
          </div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-6">
            What are you dressing as?
          </h1>
          <form onSubmit={submit} className="flex max-w-2xl">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="vampire, mario, 80s, balloon..."
              data-testid="search-input"
              className="flex-1 brut bg-white px-4 py-3 text-lg font-medium focus:outline-none"
            />
            <button
              type="submit"
              data-testid="search-submit"
              className="brut pcs-bg-pink text-white px-6 -ml-[2px] font-bold uppercase tracking-wide brut-press"
            >
              Search
            </button>
          </form>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 space-y-12">
        {loading && (
          <div className="font-mono uppercase text-xs tracking-widest opacity-70">
            Searching...
          </div>
        )}

        {!loading && initialQ && (
          <>
            <div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-none mb-6">
                Products{" "}
                <span className="font-mono text-base align-middle opacity-60">
                  ({products.length})
                </span>
              </h2>
              {products.length === 0 ? (
                <div className="brut bg-white p-6 font-mono text-sm">
                  No products matched "{initialQ}".
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {products.map((p) => (
                    <ProductCard key={p.slug} product={p} />
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-none mb-6">
                Articles{" "}
                <span className="font-mono text-base align-middle opacity-60">
                  ({articles.length})
                </span>
              </h2>
              {articles.length === 0 ? (
                <div className="brut bg-white p-6 font-mono text-sm">
                  No articles matched "{initialQ}".
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {articles.map((a) => (
                    <ArticleCard key={a.slug} article={a} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
